import { useCallback, useEffect, useRef, useState } from 'react';
import { debugLog } from '../../utils/debugLog';
import { normalizeFps, stepFrames as stepFrameTime } from '../../utils/timecode';

/**
 * State and transport for one HTML <video>, shared by the annotation studio
 * and the editor workspace so both players behave the same: frame stepping,
 * J/K/L shuttle (reverse is simulated by stepping, since browsers cannot play
 * backwards), loop between in/out marks, and a playhead that updates smoothly
 * while playing rather than at `timeupdate`'s ~4Hz.
 */

export interface LoopRange {
  enabled: boolean;
  start: number | null;
  end: number | null;
}

export interface MediaPlayerOptions {
  fps?: number;
  loop?: LoopRange;
}

export interface MediaPlayer {
  /** Callback ref for the <video>. */
  ref: (node: HTMLVideoElement | null) => void;
  element: HTMLVideoElement | null;
  currentTime: number;
  duration: number;
  playing: boolean;
  buffering: boolean;
  error: string | null;
  volume: number;
  muted: boolean;
  /** Playback speed chosen in the speed menu. */
  rate: number;
  /** J/K/L shuttle speed: negative is reverse, 0 when not shuttling. */
  shuttle: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  skip: (deltaSeconds: number) => void;
  stepFrames: (frames: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setRate: (rate: number) => void;
  shuttleForward: () => void;
  shuttleReverse: () => void;
  /** Exact time from the element (state can lag a frame behind). */
  getTime: () => number;
}

const UI_UPDATE_MS = 50;
const SHUTTLE_LEVELS = [1, 2, 4];
/** Some files (e.g. MediaRecorder WebM) only report a duration late. */
const DURATION_POLL_MS = 500;
const DURATION_POLL_ATTEMPTS = 20;

const finiteDuration = (value: number) => (Number.isFinite(value) && value > 0 ? value : 0);
const finiteTime = (value: number) => (Number.isFinite(value) && value > 0 ? value : 0);

const describeMediaError = (el: HTMLVideoElement): string => {
  const code = el.error?.code;
  if (code === 2) return 'A network error stopped the video from loading.';
  if (code === 3) return 'The video could not be decoded.';
  if (code === 4) return 'This video format or link is not supported by your browser.';
  return 'The video could not be loaded.';
};

const safePlay = (el: HTMLVideoElement) => {
  try {
    const result = el.play() as Promise<void> | undefined;
    if (result && typeof result.catch === 'function') {
      result.catch((err: unknown) => debugLog('Video play() was rejected:', err));
    }
  } catch (err) {
    debugLog('Video play() threw:', err);
  }
};

export function useMediaPlayer({ fps, loop }: MediaPlayerOptions = {}): MediaPlayer {
  const rateFps = normalizeFps(fps);
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(1);
  const [muted, setMuted] = useState(false);
  const [rate, setRateState] = useState(1);
  const [shuttle, setShuttle] = useState(0);

  const loopRef = useRef<LoopRange | undefined>(loop);
  loopRef.current = loop;
  const rateRef = useRef(rate);
  rateRef.current = rate;
  const reverseTimerRef = useRef<number | null>(null);
  const shuttleRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  const ref = useCallback((node: HTMLVideoElement | null) => setElement(node), []);

  const applyShuttle = useCallback((level: number) => {
    shuttleRef.current = level;
    setShuttle(level);
  }, []);

  const stopReverse = useCallback(() => {
    if (reverseTimerRef.current !== null) {
      window.clearInterval(reverseTimerRef.current);
      reverseTimerRef.current = null;
    }
  }, []);

  // Media element events.
  useEffect(() => {
    const el = element;
    // A shuttle stepping the previous element must not keep running.
    stopReverse();
    applyShuttle(0);
    if (!el) {
      // The <video> went away (unmounted or replaced): nothing is playing.
      setPlaying(false);
      setBuffering(false);
      return undefined;
    }

    // Start from what this element is doing, not what the last one did.
    setPlaying(!el.paused && !el.ended);
    setBuffering(false);
    setError(el.error ? describeMediaError(el) : null);
    setCurrentTime(finiteTime(el.currentTime));
    setDuration(finiteDuration(el.duration));

    let poll: number | null = null;
    const stopDurationPoll = () => {
      if (poll !== null) window.clearInterval(poll);
      poll = null;
    };
    const startDurationPoll = () => {
      stopDurationPoll();
      let attempts = 0;
      poll = window.setInterval(() => {
        attempts += 1;
        const value = finiteDuration(el.duration);
        if (value) {
          setDuration(value);
          stopDurationPoll();
        } else if (attempts >= DURATION_POLL_ATTEMPTS) {
          stopDurationPoll();
        }
      }, DURATION_POLL_MS);
    };

    const updateDuration = () => {
      const value = finiteDuration(el.duration);
      if (value) setDuration(value);
    };
    const onTime = () => {
      setCurrentTime(el.currentTime);
      updateDuration();
    };
    const onPlay = () => {
      setPlaying(true);
      setError(null);
    };
    const onPlaying = () => {
      setPlaying(true);
      setBuffering(false);
    };
    const onPause = () => {
      setPlaying(false);
      setCurrentTime(el.currentTime);
    };
    const onWaiting = () => setBuffering(true);
    const onCanPlay = () => {
      setBuffering(false);
      updateDuration();
    };
    const onError = () => {
      setError(describeMediaError(el));
      setBuffering(false);
      setPlaying(false);
    };
    const onVolume = () => {
      setVolumeState(el.volume);
      setMuted(el.muted);
    };
    const onLoadStart = () => setError(null);
    // A new source (`src` swapped, or load() called) resets the element
    // without a `pause` event: the browser sets `paused`, returns the position
    // to 0, the duration to NaN and the speed to the default. Mirror that, or
    // the transport keeps showing the old source as playing.
    const onEmptied = () => {
      stopReverse();
      applyShuttle(0);
      setPlaying(false);
      setBuffering(false);
      setError(null);
      setCurrentTime(finiteTime(el.currentTime));
      setDuration(finiteDuration(el.duration));
      el.playbackRate = rateRef.current;
      startDurationPoll();
    };

    el.addEventListener('loadedmetadata', updateDuration);
    el.addEventListener('durationchange', updateDuration);
    el.addEventListener('loadeddata', updateDuration);
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('seeked', onTime);
    el.addEventListener('play', onPlay);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onPause);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('error', onError);
    el.addEventListener('volumechange', onVolume);
    el.addEventListener('loadstart', onLoadStart);
    el.addEventListener('emptied', onEmptied);

    if (el.readyState >= 1) updateDuration();
    startDurationPoll();

    return () => {
      stopDurationPoll();
      el.removeEventListener('loadedmetadata', updateDuration);
      el.removeEventListener('durationchange', updateDuration);
      el.removeEventListener('loadeddata', updateDuration);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('seeked', onTime);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onPause);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('error', onError);
      el.removeEventListener('volumechange', onVolume);
      el.removeEventListener('loadstart', onLoadStart);
      el.removeEventListener('emptied', onEmptied);
    };
  }, [element, stopReverse, applyShuttle]);

  // Smooth playhead + loop between marks while playing.
  useEffect(() => {
    const el = element;
    if (!el || !playing) return undefined;
    let last = 0;
    const raf = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (cb: FrameRequestCallback) => window.setTimeout(() => cb(performance.now()), 16);
    const caf = typeof window.cancelAnimationFrame === 'function'
      ? window.cancelAnimationFrame.bind(window)
      : (id: number) => window.clearTimeout(id);
    const tick = (now: number) => {
      const range = loopRef.current;
      if (
        range?.enabled &&
        typeof range.start === 'number' &&
        typeof range.end === 'number' &&
        range.end > range.start &&
        el.currentTime >= range.end
      ) {
        el.currentTime = range.start;
      }
      if (now - last >= UI_UPDATE_MS) {
        last = now;
        setCurrentTime(el.currentTime);
      }
      frameRef.current = raf(tick) as unknown as number;
    };
    frameRef.current = raf(tick) as unknown as number;
    return () => {
      if (frameRef.current !== null) caf(frameRef.current);
      frameRef.current = null;
    };
  }, [element, playing]);

  // Volume and mute are owned here; the element follows.
  useEffect(() => {
    if (!element) return;
    element.volume = Math.min(1, Math.max(0, volume));
    element.muted = muted;
  }, [element, volume, muted]);

  useEffect(() => {
    if (element && shuttle <= 0) element.playbackRate = rate;
  }, [element, rate, shuttle]);

  useEffect(() => () => stopReverse(), [stopReverse]);

  const clampTime = useCallback((seconds: number) => {
    const max = finiteDuration(element?.duration ?? 0) || duration || Number.POSITIVE_INFINITY;
    return Math.min(max, Math.max(0, Number.isFinite(seconds) ? seconds : 0));
  }, [element, duration]);

  const seek = useCallback((seconds: number) => {
    if (!element) return;
    const target = clampTime(seconds);
    try {
      element.currentTime = target;
    } catch (err) {
      debugLog('Seek failed before metadata loaded:', err);
    }
    setCurrentTime(target);
  }, [element, clampTime]);

  const pause = useCallback(() => {
    stopReverse();
    applyShuttle(0);
    if (!element) return;
    element.pause();
    element.playbackRate = rateRef.current;
  }, [element, stopReverse, applyShuttle]);

  const play = useCallback(() => {
    stopReverse();
    applyShuttle(0);
    if (!element) return;
    element.playbackRate = rateRef.current;
    const range = loopRef.current;
    if (
      range?.enabled && typeof range.start === 'number' && typeof range.end === 'number' &&
      range.end > range.start && (element.currentTime >= range.end || element.currentTime < range.start)
    ) {
      element.currentTime = range.start;
    }
    safePlay(element);
  }, [element, stopReverse, applyShuttle]);

  const toggle = useCallback(() => {
    if (!element) return;
    if (element.paused || reverseTimerRef.current !== null) {
      if (reverseTimerRef.current !== null) {
        pause();
        return;
      }
      play();
    } else {
      pause();
    }
  }, [element, play, pause]);

  const skip = useCallback((deltaSeconds: number) => {
    if (!element) return;
    seek(element.currentTime + deltaSeconds);
  }, [element, seek]);

  const stepFrames = useCallback((frames: number) => {
    if (!element) return;
    if (!element.paused) element.pause();
    stopReverse();
    applyShuttle(0);
    seek(stepFrameTime(element.currentTime, frames, rateFps, finiteDuration(element.duration) || duration || undefined));
  }, [element, seek, stopReverse, applyShuttle, rateFps, duration]);

  const setVolume = useCallback((next: number) => {
    const value = Math.min(1, Math.max(0, next));
    setVolumeState(value);
    if (value > 0) setMuted(false);
  }, []);

  const toggleMute = useCallback(() => setMuted((value) => !value), []);

  const setRate = useCallback((next: number) => {
    setRateState(next);
    if (element) element.playbackRate = next;
  }, [element]);

  const shuttleForward = useCallback(() => {
    if (!element) return;
    stopReverse();
    const current = shuttleRef.current;
    const nextLevel = current <= 0
      ? 1
      : SHUTTLE_LEVELS[Math.min(SHUTTLE_LEVELS.length - 1, SHUTTLE_LEVELS.indexOf(current) + 1)];
    element.playbackRate = nextLevel === 1 ? rateRef.current : nextLevel;
    applyShuttle(nextLevel);
    if (element.paused) safePlay(element);
  }, [element, stopReverse, applyShuttle]);

  const shuttleReverse = useCallback(() => {
    if (!element) return;
    if (!element.paused) element.pause();
    const current = shuttleRef.current;
    const nextLevel = current >= 0
      ? -1
      : -SHUTTLE_LEVELS[Math.min(SHUTTLE_LEVELS.length - 1, SHUTTLE_LEVELS.indexOf(-current) + 1)];
    stopReverse();
    const stepMs = 50;
    reverseTimerRef.current = window.setInterval(() => {
      const next = element.currentTime - (stepMs / 1000) * Math.abs(nextLevel);
      if (next <= 0) {
        element.currentTime = 0;
        setCurrentTime(0);
        stopReverse();
        applyShuttle(0);
        return;
      }
      element.currentTime = next;
      setCurrentTime(next);
    }, stepMs);
    applyShuttle(nextLevel);
  }, [element, stopReverse, applyShuttle]);

  const getTime = useCallback(() => (element ? element.currentTime : 0), [element]);

  return {
    ref,
    element,
    currentTime,
    duration,
    playing,
    buffering,
    error,
    volume,
    muted,
    rate,
    shuttle,
    play,
    pause,
    toggle,
    seek,
    skip,
    stepFrames,
    setVolume,
    toggleMute,
    setRate,
    shuttleForward,
    shuttleReverse,
    getTime,
  };
}
